import { Uuid } from "@/domains/global/identifiers";
import type {
  AssistantTaskChangeRequestModelData,
  AssistantTaskModelData,
} from "@/store/assistant/types";
import { AssistantChangeRequestChangeKind } from "@/store/assistant/types";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import { computed, makeObservable, observable } from "mobx";

export class AssistantTaskObservable extends BaseSyncModel<AssistantTaskModelData> {
  public modelKind = SyncModelKind.AssistantTask;

  constructor({
    id,
    data,
    store,
  }: { id: Uuid; data: SyncUpdateValue<AssistantTaskModelData> } & WithAppStore) {
    super({ id, data, store });
    makeObservable(this, {
      modelKind: observable,
      assistantId: computed,
      projectId: computed,
      title: computed,
      description: computed,
      realStatus: computed,
      status: computed,
      dueAt: computed,
      createdAt: computed,
      updatedAt: computed,
      pendingChangeRequests: computed,
      latestPendingChangeRequest: computed,
      hasPendingDelete: computed,
      hasPendingStatusChange: computed,
    });
  }

  get assistantId(): Uuid {
    return this.modelData.assistant_id;
  }

  get projectId(): Uuid | null {
    return this.modelData.project_id;
  }

  get title(): string {
    return this.modelData.title;
  }

  get description(): string | null {
    return this.modelData.description;
  }

  get realStatus(): string {
    return this.modelData.status;
  }

  /**
   * Status as projected through any pending change requests targeting this task.
   * Falls back to the underlying real status when no pending update applies.
   */
  get status(): string {
    const latest = this.latestPendingChangeRequest;
    if (latest) {
      const data = latest.modelData;
      if (
        data.change_kind === AssistantChangeRequestChangeKind.Update &&
        data.requested_status != null &&
        data.changed_fields.includes("status")
      ) {
        return data.requested_status;
      }
    }
    return this.realStatus;
  }

  get dueAt(): string | null {
    return this.modelData.due_at;
  }

  get createdAt(): string {
    return this.modelData.created_at;
  }

  get updatedAt(): string {
    return this.modelData.updated_at;
  }

  get pendingChangeRequests() {
    return this.store.assistantTaskChangeRequests.all.filter(
      request => request.modelData.target_task_id === this.id
    );
  }

  get latestPendingChangeRequest(): { modelData: AssistantTaskChangeRequestModelData } | undefined {
    const requests = this.pendingChangeRequests;
    if (requests.length === 0) return undefined;
    return requests.reduce((latest, current) =>
      current.modelData.requested_at > latest.modelData.requested_at ? current : latest
    );
  }

  get hasPendingDelete(): boolean {
    return this.pendingChangeRequests.some(
      request => request.modelData.change_kind === AssistantChangeRequestChangeKind.Delete
    );
  }

  get hasPendingStatusChange(): boolean {
    return this.status !== this.realStatus;
  }
}
